import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import * as moment from 'moment-timezone';

@Injectable({
  providedIn: 'root',
})
export class RestfulEndpoint {
  constructor(protected httpClient: HttpClient) {}

  /**
   * Create new record
   *
   * @param endpoint
   * @param {object|FormData} data
   * @param header
   * @param domain
   * @param responseType
   * @param options Object - optional options
   * @param options.useAuthMiddleware boolean - default is false - if true, the request will be sent to the authorization API instead of the main API
   * @param options.authMiddlewareVersion string - default is 'v1' - the version of the authorization API to use
   * @param options.version string - default is 'v2' - the version of the API to use, version is only used if domain is passed
   * @returns {Observable}
   */

  public post(
    endpoint: string,
    data: any,
    header?: any,
    domain?: string,
    responseType?: any,
    options?: { useAuthMiddleware?: boolean; authMiddlewareVersion?: string; version?: string },
  ): Observable<any> {
    const defaultOptions = {
      useAuthMiddleware: false,
      authMiddlewareVersion: 'v1',
      version: 'v2',
    };
    options = { ...defaultOptions, ...options };
    const currentLang = localStorage.getItem('lang') || 'en';
    const timeZone = moment.tz.guess();
    const headers: HttpHeaders = header
      ? header
      : new HttpHeaders({
          ...header,
          userLang: currentLang,
          timeZone: timeZone,
          platform: 'web',
          os: 'web',
          //TODO:
        });

    let url = '';

    if (options.useAuthMiddleware) {
      url = `${environment.authorization_api_base_url}/${options.authMiddlewareVersion}/${endpoint}`;
    } else {
      const normalizedVersion = options.version ? options.version.replace(/\/+$/, '') + '/' : '';
      const normalizedDomain = domain ? domain.replace(/\/+$/, '') + '/' : '';
      url = normalizedDomain
        ? `${environment.api_base_url}/${normalizedVersion}${normalizedDomain}${endpoint}`
        : `${environment.api_base_url}/${endpoint}`;
    }

    return responseType
      ? this.httpClient.post(url, data, { headers, responseType: responseType })
      : this.httpClient.post(url, data, { headers });
  }

  /**
   * Fetch one record from endpoint api
   *
   * @returns {Observable}
   * @param endpoint
   * @param queryParams
   * @param domain
   * @param options
   * @param options.useAuthMiddleware boolean - default is false - if true, the request will be sent to the authorization API instead of the main API
   * @param options.authMiddlewareVersion string - default is 'v1' - the version of the authorization API to use
   * @param options.version string - default is 'v2' - the version of the API to use, version is only used if domain is passed
   * @param header
   */
  // I've changed id from type number to string so it can be used for apiKey for Role , the parameters are strings.
  // All Numbers could be string but not vice versa .
  public get(
    endpoint: string,
    queryParams?: any,
    domain?: string,
    options?: {
      version?: string | null;
      useAuthMiddleware?: boolean;
      authMiddlewareVersion?: string;
    },
    header?: any,
  ): Observable<any> {
    const defaultOptions = {
      version: 'v2',
      useAuthMiddleware: false,
      authMiddlewareVersion: 'v1',
    };
    options = { ...defaultOptions, ...options };
    const currentLang = localStorage.getItem('lang') || 'en';
    const timeZone = moment.tz.guess();
    const headers: HttpHeaders = header
      ? header
      : new HttpHeaders({
          ...header,
          userLang: currentLang,
          timeZone: timeZone,
          platform: 'web',
        });

    let url = '';

    if (options.useAuthMiddleware) {
      url = `${environment.authorization_api_base_url}/${options.authMiddlewareVersion}/${endpoint}`;
    } else {
      const normalizedVersion = options.version ? options.version.replace(/\/+$/, '') + '/' : '';
      const normalizedDomain = domain ? domain.replace(/\/+$/, '') + '/' : '';
      url = normalizedDomain
        ? `${environment.api_base_url}/${normalizedVersion}${normalizedDomain}${endpoint}`
        : `${environment.api_base_url}/${normalizedVersion}${endpoint}`;
    }

    if (queryParams) {
      let queryParamString = '';
      for (const key in queryParams) {
        if (queryParams.hasOwnProperty(key)) {
          const value = queryParams[key];
          if (value !== undefined) {
            queryParamString += `${key}=${encodeURIComponent(value)}&`;
          }
        }
      }
      if (queryParamString) {
        queryParamString = queryParamString.slice(0, -1); // Remove the trailing '&'
        url += `?${queryParamString}`;
      }
    }

    return this.httpClient.get(url, {
      headers,
    });
  }

  /**
   * Update existing record
   *
   * @param endpoint
   * @param   {object|FormData} data
   * @param header
   * @param domain
   * @param authorization
   * @param options.useAuthMiddleware boolean - default is false - if true, the request will be sent to the authorization API instead of the main API
   * @param options.authMiddlewareVersion string - default is 'v1' - the version of the authorization API to use
   * @returns {Observable}
   */
  public put(
    endpoint: string,
    data: any,
    header?: any,
    domain?: string,
    options?: { useAuthMiddleware?: boolean; authMiddlewareVersion?: string; version?: string },
  ): Observable<any> {
    const defaultOptions = {
      version: 'v2',
      useAuthMiddleware: false,
      authMiddlewareVersion: 'v1',
    };
    options = { ...defaultOptions, ...options };
    const currentLang = localStorage.getItem('lang') || 'en';
    const timeZone = moment.tz.guess();
    const headers: HttpHeaders = header
      ? header
      : new HttpHeaders({
          ...header,
          userLang: currentLang,
          timeZone: timeZone,
          platform: 'web',
        });

    let url = '';
    if (options.useAuthMiddleware) {
      url = `${environment.authorization_api_base_url}/${options.authMiddlewareVersion}/${endpoint}`;
    } else {
      //if domain is passed we need to use default version unless version is passed
      const normalizedDomain = domain ? domain.replace(/\/+$/, '') + '/' : '';
      const normalizedVersion = options.version ? options.version.replace(/\/+$/, '') + '/' : '';
      url = normalizedDomain
        ? `${environment.api_base_url}/${normalizedVersion}${normalizedDomain}${endpoint}`
        : `${environment.api_base_url}/${endpoint}`;
    }

    return this.httpClient.put(url, data, { headers });
  }

  /**
   * Partially update existing record
   *
   * @param endpoint
   * @param data
   * @param header
   * @param domain
   * @param options
   * @returns {Observable}
   */
  public patch(
    endpoint: string,
    data: any,
    header?: any,
    domain?: string,
    options?: { useAuthMiddleware?: boolean; authMiddlewareVersion?: string; version?: string },
  ): Observable<any> {
    const defaultOptions = {
      version: 'v2',
      useAuthMiddleware: false,
      authMiddlewareVersion: 'v1',
    };
    options = { ...defaultOptions, ...options };
    const currentLang = localStorage.getItem('lang') || 'en';
    const timeZone = moment.tz.guess();
    const headers: HttpHeaders = header
      ? header
      : new HttpHeaders({
          ...header,
          userLang: currentLang,
          timeZone: timeZone,
          platform: 'web',
        });

    let url = '';
    if (options.useAuthMiddleware) {
      url = `${environment.authorization_api_base_url}/${options.authMiddlewareVersion}/${endpoint}`;
    } else {
      const normalizedDomain = domain ? domain.replace(/\/+$/, '') + '/' : '';
      const normalizedVersion = options.version ? options.version.replace(/\/+$/, '') + '/' : '';
      url = normalizedDomain
        ? `${environment.api_base_url}/${normalizedVersion}${normalizedDomain}${endpoint}`
        : `${environment.api_base_url}/${endpoint}`;
    }

    return this.httpClient.patch(url, data, { headers });
  }

  /**
   * Delete existing record
   *
   * @returns {Observable}
   * @param endpoint
   * @param header
   * @param options
   * @param options.useAuthMiddleware boolean - default is false - if true, the request will be sent to the authorization API instead of the main API
   * @param options.authMiddlewareVersion string - default is 'v1' - the version of the authorization API to use
   */
  public delete(
    endpoint: string,
    header: any = {},
    options?: { useAuthMiddleware?: boolean; authMiddlewareVersion?: string },
  ): Observable<any> {
    const defaultOptions = {
      useAuthMiddleware: false,
      authMiddlewareVersion: 'v1',
    };
    options = { ...defaultOptions, ...options };
    const currentLang = localStorage.getItem('lang') || 'en';
    const timeZone = moment.tz.guess();
    const headers: HttpHeaders = header
      ? header
      : new HttpHeaders({
          ...header,
          userLang: currentLang,
          timeZone: timeZone,
          platform: 'web',
        });

    let url = '';

    if (options.useAuthMiddleware) {
      url = `${environment.authorization_api_base_url}/${options.authMiddlewareVersion}/${endpoint}`;
    } else {
      url = `${environment.api_base_url}/${endpoint}`;
    }

    return this.httpClient.delete(url, {
      headers,
    });
  }
}
